// components/customer/SearchProductCard.tsx
import React from 'react';
import { Star, TrendingUp, ChevronRight, Palette, Package } from 'lucide-react';
import type { SearchResult } from '../../api/search';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '../../utils/utilityFunctions';
import AddToCartButton from '../common/AddToCartButton';

interface SearchProductCardProps {
  product: SearchResult;
  showRelevance?: boolean;
  layout?: 'horizontal' | 'vertical';
}

const SearchProductCard: React.FC<SearchProductCardProps> = ({
  product,
  showRelevance = true,
  layout = 'horizontal'
}) => {
  const navigate = useNavigate();

  const getRelevanceBadge = () => {
    if (!product?.relevance || !showRelevance) return null;

    if (product.relevance > 8) {
      return { text: 'Perfect Match', color: 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm' };
    } else if (product.relevance > 6) {
      return { text: 'Highly Relevant', color: 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm' };
    } else if (product.relevance > 4) {
      return { text: 'Good Match', color: 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-sm' };
    }
    return null;
  };

  const badge = getRelevanceBadge();
  const finalPrice = product.discounted_price || product.selling_price;
  const originalPrice = product.discount_percent ? product.selling_price : null;
  const discountPercent = product.discount_percent;

  const hasVariants = product.variants && product.variants.length > 0;
  const hasImage = product.images && product.images.length > 0;
  const isFeatured = product.is_featured;
  const isNew = false;

  if (layout === 'vertical') {
    return (
      <div
        className="group bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
        onClick={() => navigate(`/product/${product.id}`)}
      >
        {/* Image Container */}
        <div className="relative h-48 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
          {hasImage ? (
            <img
              src={product.images![0].image_url}
              alt={product.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="h-16 w-16 text-gray-300" />
            </div>
          )}

          {/* Top badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-1">
            {isFeatured && (
              <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
                Featured
              </span>
            )}
            {isNew && (
              <span className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
                New
              </span>
            )}
            {discountPercent && (
              <span className="bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
                -{discountPercent}% OFF
              </span>
            )}
          </div>

          {/* Relevance badge */}
          {badge && (
            <div className="absolute top-3 right-3">
              <span className={`text-xs font-semibold px-2 py-1 rounded-full shadow-md ${badge.color}`}>
                {badge.text}
              </span>
            </div>
          )}

          {/* Variant indicator */}
          {hasVariants && (
            <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1 shadow-sm">
              <Palette className="h-3 w-3" />
              {product.variants?.length} colors
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1 group-hover:text-blue-600 transition-colors">
            {product.title}
          </h3>

          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
            {product.description}
          </p>

          {/* Price & Rating */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-gray-900">
                {formatPrice(finalPrice)}
              </span>
              {originalPrice && (
                <span className="text-sm text-gray-500 line-through">
                  {formatPrice(originalPrice)}
                </span>
              )}
            </div>

            {product.rating && (
              <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-full">
                <Star className="h-3 w-3 text-amber-400 fill-current" />
                <span className="text-xs font-semibold text-gray-700">
                  {product.rating.toFixed(1)}
                </span>
              </div>
            )}
          </div>

          {/* Add to Cart Button */}
          <AddToCartButton
            product={product}
            variants={product.variants || []}
            availableQuantity={product.available_quantity || 0}
            size="md"
            variant="primary"
            showText={true}
            className="w-full justify-center"
          />
        </div>
      </div>
    );
  }

  // Horizontal layout - MOBILE FIRST with responsive desktop
  return (
    <div
      className="group bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer"
      onClick={() => navigate(`/product/${product.id}`)}
    >
      {/* MOBILE LAYOUT (default) */}
      <div className="flex p-3 gap-3 md:hidden">
        {/* Image Section - Mobile */}
        <div className="relative flex-shrink-0 w-20 h-20 rounded-md overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
          {hasImage ? (
            <img
              src={product.images![0].image_url}
              alt={product.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="h-8 w-8 text-gray-300" />
            </div>
          )}

          {/* Variant indicator - Mobile */}
          {hasVariants && (
            <div className="absolute bottom-1 right-1 bg-white/95 text-gray-700 text-[10px] font-medium px-1 py-0.5 rounded flex items-center gap-0.5 shadow-xs">
              <Palette className="h-2 w-2" />
              {product.variants?.length}
            </div>
          )}
        </div>

        {/* Content Section - Mobile */}
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          {/* Top row: Title and badges */}
          <div className="mb-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-medium text-gray-900 text-sm line-clamp-2 group-hover:text-blue-600 transition-colors leading-tight">
                {product.title}
              </h3>
              <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>

            {/* Compact badges row - Mobile */}
            <div className="flex flex-wrap items-center gap-1 mt-1">
              {badge && (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge.color}`}>
                  {badge.text}
                </span>
              )}

              {isFeatured && (
                <span className="bg-amber-100 text-amber-800 text-[10px] font-semibold px-1.5 py-0.5 rounded">
                  Featured
                </span>
              )}

              {discountPercent && (
                <span className="bg-red-100 text-red-700 text-[10px] font-semibold px-1.5 py-0.5 rounded">
                  -{discountPercent}%
                </span>
              )}
            </div>
          </div>

          {/* Middle row: Price and rating - Mobile */}
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-baseline gap-1">
              <span className="text-base font-bold text-gray-900">
                {formatPrice(finalPrice)}
              </span>
              {originalPrice && (
                <span className="text-xs text-gray-500 line-through ml-1">
                  {formatPrice(originalPrice)}
                </span>
              )}
            </div>

            {product.rating && (
              <div className="flex items-center gap-1 bg-gray-50 px-2 py-0.5 rounded">
                <Star className="h-3 w-3 text-amber-400 fill-current" />
                <span className="text-xs font-semibold text-gray-700">
                  {product.rating.toFixed(1)}
                </span>
              </div>
            )}
          </div>

          {/* Bottom row: Add to cart button - Mobile */}
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500 truncate mr-2">
              {product.category_tags?.[0] || 'Product'}
            </div>

            <div onClick={(e) => e.stopPropagation()}>
              <AddToCartButton
                product={product}
                variants={product.variants}
                availableQuantity={product.available_quantity}
                size="sm"
                variant="primary"
                showText={false}
                className="shadow-sm hover:shadow"
              />
            </div>
          </div>
        </div>
      </div>

      {/* DESKTOP LAYOUT (md and up) */}
      <div className="hidden md:flex p-4 gap-4">
        {/* Image Section - Desktop */}
        <div className="relative flex-shrink-0 w-32 h-32 rounded-lg overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
          {hasImage ? (
            <img
              src={product.images![0].image_url}
              alt={product.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="h-10 w-10 text-gray-300" />
            </div>
          )}

          {/* Variant indicator - Desktop */}
          {hasVariants && (
            <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium px-1.5 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
              <Palette className="h-2.5 w-2.5" />
              {product.variants?.length}
            </div>
          )}
        </div>

        {/* Content Section - Desktop */}
        <div className="flex-1 min-w-0">
          {/* Header with badges - Desktop */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                  {product.title}
                </h3>
                {isFeatured && (
                  <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0">
                    Featured
                  </span>
                )}
              </div>

              {/* Tags and badges - Desktop */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {badge && (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badge.color}`}>
                    {badge.text}
                  </span>
                )}

                {product.category_tags && product.category_tags.slice(0, 2).map(tag => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}

                {product.view_count > 100 && (
                  <div className="flex items-center gap-1 text-gray-500 text-xs">
                    <TrendingUp className="h-3 w-3" />
                    <span>Popular</span>
                  </div>
                )}
              </div>
            </div>

            <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>

          {/* Description - Desktop */}
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
            {product.description}
          </p>

          {/* Bottom section: Price, Rating, and Actions - Desktop */}
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-3">
              <div className="flex flex-col">
                <span className="text-xl font-bold text-gray-900">
                  {formatPrice(finalPrice)}
                </span>
                {originalPrice && (
                  <span className="text-sm text-gray-500 line-through">
                    {formatPrice(originalPrice)}
                  </span>
                )}
              </div>

              {discountPercent && (
                <span className="bg-gradient-to-r from-red-50 to-red-100 text-red-700 text-sm font-semibold px-2 py-1 rounded-lg">
                  Save {discountPercent}%
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              {product.rating && (
                <div className="flex items-center gap-1 bg-gray-50 px-3 py-1.5 rounded-full">
                  <Star className="h-4 w-4 text-amber-400 fill-current" />
                  <span className="text-sm font-semibold text-gray-700">
                    {product.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-gray-500">({product.view_count || 0})</span>
                </div>
              )}

              <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                <AddToCartButton
                  product={{
                    id: product.id,
                    title: product.title,
                    selling_price: finalPrice, // This becomes 'price' in CartItem
                    sku: product.sku || '',
                    images: product.images
                  }}
                  variants={product.variants}
                  availableQuantity={product.available_quantity}
                  size="md"
                  variant="primary"
                  showText="desktop"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchProductCard;